import * as Highcharts from 'highcharts';

/**
 * Y-axis format types used across the application
 */
export type YAxisFormatType = 'percentage' | 'score' | 'compact' | 'default';

/**
 * Configuration for a Y-axis based on data type
 */
export interface YAxisConfig {
  format: YAxisFormatType;
  min?: number;
  max?: number;
  allowDecimals?: boolean;
  tickInterval?: number;
  tickAmount?: number;
  endOnTick?: boolean;
  startOnTick?: boolean;
  maxPadding?: number;
  opposite?: boolean;
}

const PERCENTAGE_SCORE_TYPES = ['sentiment'];

const NEGATIVE_SCORE_TYPES = ['bull_bear', 'bb'];

/**
 * Returns Y-axis configuration based on score type.
 * This standardizes Y-axis formatting across the application.
 *
 * @param scoreType - The score type (sentiment, general_risk, esg, etc.)
 * @returns Y-axis configuration object
 */
export function getYAxisConfigForScoreType(scoreType: string): YAxisConfig {
  if (PERCENTAGE_SCORE_TYPES.includes(scoreType)) {
    return {
      format: 'percentage',
      min: null,
      max: null,

      maxPadding: 0,
    };
  }

  if (NEGATIVE_SCORE_TYPES.includes(scoreType)) {
    return {
      format: 'score',
      min: 0,
      max: null,
      allowDecimals: false,
      opposite: true,
      maxPadding: 0.1,
    };
  }

  return {
    format: 'score',
    min: 0,
    max: null,
    allowDecimals: false,
    opposite: true,
    maxPadding: 0.1,
    endOnTick: false,
    tickInterval: 1,
  };
}

/**
 * Returns a Highcharts label formatter function based on format type.
 * Matches the formatting used in news-chart.component.ts
 *
 * @param format - The format type
 * @returns Highcharts formatter function
 */
export function getYAxisLabelFormatter(
  format: YAxisFormatType,
): Highcharts.AxisLabelsFormatterCallbackFunction | undefined {
  switch (format) {
    case 'percentage':
      return function () {
        return Math.round((this.value as number) * 100) + '%';
      };

    case 'compact':
      return function () {
        const value = this.value as number;
        if (Math.abs(value) >= 1000000) {
          return (value / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
        }
        if (Math.abs(value) >= 1000) {
          return (value / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
        }
        return String(value);
      };

    case 'score':
      return function () {
        return this.value as unknown as string;
      };

    case 'default':
    default:
      return undefined;
  }
}

/**
 * Builds complete Highcharts Y-axis options for a score axis
 *
 * @param scoreType - The score type
 * @param title - Optional axis title
 * @param opposite - Whether axis is on opposite (right) side
 * @returns Highcharts Y-axis options
 */
export function buildScoreYAxisOptions(
  scoreType: string,
  title?: string,
  opposite = true,
): Highcharts.YAxisOptions {
  const config = getYAxisConfigForScoreType(scoreType);
  const formatter = getYAxisLabelFormatter(config.format);

  const options: Highcharts.YAxisOptions = {
    title: { text: title || '' },
    opposite,
    gridLineColor: 'rgba(255, 255, 255, 0.05)',
    gridLineWidth: 0,
    lineColor: 'rgba(255, 255, 255, 0.05)',
    lineWidth: 2,
    labels: {
      align: 'left',
      style: {
        color: 'rgba(255, 255, 255, 0.5)',
        fontWeight: 'normal',
      },
      ...(formatter && { formatter }),
    },
  };

  if (config.min !== undefined) options.min = config.min;
  if (config.max !== undefined) options.max = config.max;
  if (config.maxPadding !== undefined) options.maxPadding = config.maxPadding;
  if (config.allowDecimals !== undefined)
    options.allowDecimals = config.allowDecimals;
  if (config.tickInterval !== undefined)
    options.tickInterval = config.tickInterval;
  if (config.endOnTick !== undefined) options.endOnTick = config.endOnTick;
  if (config.startOnTick !== undefined)
    options.startOnTick = config.startOnTick;

  return options;
}

/**
 * Builds Y-axis options for volume/count data (left axis)
 *
 * @param title - Optional axis title
 * @returns Highcharts Y-axis options
 */
export function buildVolumeYAxisOptions(
  title?: string,
): Highcharts.YAxisOptions {
  const formatter = getYAxisLabelFormatter('compact');

  return {
    title: { text: title || '' },
    opposite: false,
    allowDecimals: false,
    endOnTick: true,
    gridLineColor: 'transparent',
    lineColor: 'rgba(255, 255, 255, 0.05)',
    lineWidth: 1,
    labels: {
      align: 'right',
      x: -3,
      ...(formatter && { formatter }),
    },
  };
}
